/**
 * Auto-Ordering Settings — Auto-Approval Rules & Dashboard
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  FormControlLabel,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as RunIcon,
  Settings as SettingsIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  HourglassEmpty as PendingIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';

// ------- Local types -------

interface AutoApprovalRule {
  id: string;
  rule_name: string;
  applies_to_category: string;
  applies_to_brand: string;
  max_order_value_mad: number;
  max_order_quantity: number;
  min_coverage_days: number;
  max_coverage_days: number;
  enforce_moq: boolean;
  round_to_case_pack: boolean;
  auto_transmit_to_erp: boolean;
  priority: number;
  is_active: boolean;
}

interface AutoApprovalLog {
  id: string;
  order_id: string;
  product_name: string;
  decision: 'approved' | 'rejected' | 'manual_review';
  reason: string;
  rule_name: string;
  quantity: number;
  value_mad: number;
  created_at: string;
}

interface AutoApprovalRateData {
  approval_rate: number;
  approved_today: number;
  pending_review: number;
}

interface RunAutoApprovalResult {
  approved: number;
  rejected: number;
  manual_review: number;
}

// ------- Default form values -------

const defaultFormData: Omit<AutoApprovalRule, 'id'> = {
  rule_name: '',
  applies_to_category: '',
  applies_to_brand: '',
  max_order_value_mad: 0,
  max_order_quantity: 0,
  min_coverage_days: 3,
  max_coverage_days: 60,
  enforce_moq: true,
  round_to_case_pack: true,
  auto_transmit_to_erp: false,
  priority: 100,
  is_active: true,
};

// ------- Component -------

const AutoOrderingSettings: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { fmtCurrencyPrecise: formatCurrency, fmtCurrencyLabel, currency } = useCurrencyFormat();

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<AutoApprovalRule | null>(null);
  const [formData, setFormData] = useState<Omit<AutoApprovalRule, 'id'>>(defaultFormData);
  const [formError, setFormError] = useState<string | null>(null);

  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<string | null>(null);

  // Notification
  const [notification, setNotification] = useState<{
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);

  // -------- Queries --------

  const {
    data: rules,
    isLoading: rulesLoading,
    error: rulesError,
  } = useQuery<AutoApprovalRule[]>({
    queryKey: ['autoApprovalRules'],
    queryFn: async () => {
      const response = await api.getAutoApprovalRules();
      const list = Array.isArray(response.data) ? response.data : response.data?.rules ?? [];
      return list.map((r: any) => ({ ...r, id: r.id ?? r.rule_id }));
    },
  });

  const { data: rateData, isLoading: rateLoading } = useQuery<AutoApprovalRateData>({
    queryKey: ['autoApprovalRate'],
    queryFn: async () => {
      const response = await api.getAutoApprovalRate();
      const d = response.data ?? {};
      let approvedToday = d.approved_today;
      let pendingReview = d.pending_review;
      if (approvedToday === undefined || pendingReview === undefined) {
        try {
          const lr = await api.getAutoApprovalLogs({ limit: 500 });
          const ll = Array.isArray(lr.data) ? lr.data : lr.data?.logs ?? [];
          const today = new Date().toISOString().slice(0, 10);
          approvedToday = approvedToday ?? ll.filter((l: any) => l.decision === 'approved' && String(l.decided_at || l.created_at || '').slice(0, 10) === today).length;
          pendingReview = pendingReview ?? ll.filter((l: any) => l.decision === 'manual_review').length;
        } catch (e) { approvedToday = approvedToday ?? 0; pendingReview = pendingReview ?? 0; }
      }
      return { ...d, approval_rate: d.approval_rate ?? d.rate_percent ?? 0, approved_today: approvedToday, pending_review: pendingReview };
    },
  });

  const {
    data: logs,
    isLoading: logsLoading,
    error: logsError,
  } = useQuery<AutoApprovalLog[]>({
    queryKey: ['autoApprovalLogs'],
    queryFn: async () => {
      const response = await api.getAutoApprovalLogs({ limit: 50 });
      const list = Array.isArray(response.data) ? response.data : response.data?.logs ?? [];
      let ruleNames: Record<string, string> = {};
      try {
        const rr = await api.getAutoApprovalRules();
        const rl = Array.isArray(rr.data) ? rr.data : rr.data?.rules ?? [];
        ruleNames = Object.fromEntries(rl.map((r: any) => [r.rule_id ?? r.id, r.rule_name]));
      } catch (e) { /* keep ids */ }
      return list.map((l: any) => ({
        ...l,
        id: l.id ?? l.log_id,
        order_id: l.order_id ?? (l.replenishment_order_id ? `RO-${String(l.replenishment_order_id).slice(0, 8).toUpperCase()}` : ''),
        product_name: l.product_name ?? l.product_sku ?? '',
        rule_name: l.rule_name ?? ruleNames[l.rule_id] ?? l.rule_id ?? '',
        quantity: l.quantity ?? l.adjusted_quantity ?? l.original_quantity ?? 0,
        value_mad: l.value_mad ?? l.order_value_mad ?? 0,
        created_at: l.created_at ?? l.decided_at,
      }));
    },
  });

  // -------- Mutations --------

  const createMutation = useMutation({
    mutationFn: (data: Omit<AutoApprovalRule, 'id'>) => api.createAutoApprovalRule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['autoApprovalRules'] });
      handleCloseDialog();
      setNotification({ message: t('autoOrdering.ruleCreated'), severity: 'success' });
    },
    onError: (error: any) => {
      setNotification({
        message: `${t('autoOrdering.errorCreating')}: ${error.response?.data?.detail || error.message}`,
        severity: 'error',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ ruleId, data }: { ruleId: string; data: Partial<AutoApprovalRule> }) =>
      api.updateAutoApprovalRule(ruleId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['autoApprovalRules'] });
      handleCloseDialog();
      setNotification({ message: t('autoOrdering.ruleUpdated'), severity: 'success' });
    },
    onError: (error: any) => {
      setNotification({
        message: `${t('autoOrdering.errorUpdating')}: ${error.response?.data?.detail || error.message}`,
        severity: 'error',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (ruleId: string) => api.deleteAutoApprovalRule(ruleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['autoApprovalRules'] });
      setDeleteConfirmOpen(false);
      setRuleToDelete(null);
      setNotification({ message: t('autoOrdering.ruleDeleted'), severity: 'success' });
    },
    onError: (error: any) => {
      setNotification({
        message: `${t('autoOrdering.errorDeleting')}: ${error.response?.data?.detail || error.message}`,
        severity: 'error',
      });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ ruleId, isActive }: { ruleId: string; isActive: boolean }) =>
      api.updateAutoApprovalRule(ruleId, { is_active: isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['autoApprovalRules'] });
    },
    onError: (error: any) => {
      setNotification({
        message: `${t('autoOrdering.errorToggling')}: ${error.response?.data?.detail || error.message}`,
        severity: 'error',
      });
    },
  });

  const runAutoApprovalMutation = useMutation({
    mutationFn: () => api.autoApprovePendingOrders(),
    onSuccess: (response) => {
      const result: RunAutoApprovalResult = response.data;
      queryClient.invalidateQueries({ queryKey: ['autoApprovalLogs'] });
      queryClient.invalidateQueries({ queryKey: ['autoApprovalRate'] });
      setNotification({
        message: t('autoOrdering.runResults', {
          approved: result.approved,
          rejected: result.rejected,
          manual: result.manual_review,
        }),
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setNotification({
        message: `${t('autoOrdering.errorRunning')}: ${error.response?.data?.detail || error.message}`,
        severity: 'error',
      });
    },
  });

  // -------- Handlers --------

  const handleOpenDialog = (rule?: AutoApprovalRule) => {
    if (rule) {
      setEditingRule(rule);
      setFormData({
        rule_name: rule.rule_name,
        applies_to_category: rule.applies_to_category,
        applies_to_brand: rule.applies_to_brand,
        max_order_value_mad: rule.max_order_value_mad,
        max_order_quantity: rule.max_order_quantity,
        min_coverage_days: rule.min_coverage_days,
        max_coverage_days: rule.max_coverage_days,
        enforce_moq: rule.enforce_moq,
        round_to_case_pack: rule.round_to_case_pack,
        auto_transmit_to_erp: rule.auto_transmit_to_erp,
        priority: rule.priority,
        is_active: rule.is_active,
      });
    } else {
      setEditingRule(null);
      setFormData({ ...defaultFormData });
    }
    setFormError(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingRule(null);
    setFormError(null);
  };

  const handleSaveRule = () => {
    if (!formData.rule_name.trim()) {
      setFormError(t('autoOrdering.ruleNameRequired'));
      return;
    }
    setFormError(null);

    if (editingRule) {
      updateMutation.mutate({ ruleId: editingRule.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDeleteRule = (ruleId: string) => {
    setRuleToDelete(ruleId);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (ruleToDelete) {
      deleteMutation.mutate(ruleToDelete);
    }
  };

  const handleToggleActive = (rule: AutoApprovalRule) => {
    toggleActiveMutation.mutate({ ruleId: rule.id, isActive: !rule.is_active });
  };

  const handleFieldChange = (field: keyof Omit<AutoApprovalRule, 'id'>, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // -------- Helpers --------

  const getDecisionChipProps = (decision: string) => {
    switch (decision) {
      case 'approved':
        return { label: t('autoOrdering.approved'), color: 'success' as const, icon: <ApprovedIcon /> };
      case 'rejected':
        return { label: t('autoOrdering.rejected'), color: 'error' as const, icon: <RejectedIcon /> };
      case 'manual_review':
        return { label: t('autoOrdering.manualReview'), color: 'warning' as const, icon: <PendingIcon /> };
      default:
        return { label: decision, color: 'default' as const };
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleString('fr-MA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });

  // -------- Render --------

  return (
    <Box>
      {/* Page header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4">{t('autoOrdering.title')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('autoOrdering.description')}
          </Typography>
        </Box>
      </Box>

      {/* ============================================================ */}
      {/* SECTION 1 — Auto-Approval Rules                              */}
      {/* ============================================================ */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">{t('autoOrdering.rules')}</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            {t('autoOrdering.addRule')}
          </Button>
        </Box>

        {rulesError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('autoOrdering.errorLoadingRules')}
          </Alert>
        )}

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('autoOrdering.colName')}</TableCell>
                <TableCell>{t('autoOrdering.colCategory')}</TableCell>
                <TableCell>{t('autoOrdering.colBrand')}</TableCell>
                <TableCell>{t('autoOrdering.colMaxValue')}</TableCell>
                <TableCell>{t('autoOrdering.colMaxQty')}</TableCell>
                <TableCell>{t('autoOrdering.colCoverageDays')}</TableCell>
                <TableCell>{t('autoOrdering.colMOQ')}</TableCell>
                <TableCell>{t('autoOrdering.colCasePack')}</TableCell>
                <TableCell>{t('autoOrdering.colActive')}</TableCell>
                <TableCell>{t('autoOrdering.colActions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rulesLoading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : rules && rules.length > 0 ? (
                rules.map((rule) => (
                  <TableRow key={rule.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {rule.rule_name}
                      </Typography>
                    </TableCell>
                    <TableCell>{rule.applies_to_category || '\u2014'}</TableCell>
                    <TableCell>{rule.applies_to_brand || '\u2014'}</TableCell>
                    <TableCell>{formatCurrency(rule.max_order_value_mad)}</TableCell>
                    <TableCell>{rule.max_order_quantity.toLocaleString()}</TableCell>
                    <TableCell>
                      {rule.min_coverage_days}{'\u2013'}{rule.max_coverage_days}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={rule.enforce_moq ? t('autoOrdering.yes') : t('autoOrdering.no')}
                        size="small"
                        color={rule.enforce_moq ? 'primary' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={rule.round_to_case_pack ? t('autoOrdering.yes') : t('autoOrdering.no')}
                        size="small"
                        color={rule.round_to_case_pack ? 'primary' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={rule.is_active}
                        onChange={() => handleToggleActive(rule)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleOpenDialog(rule)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteRule(rule.id)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Box sx={{ py: 3 }}>
                      <SettingsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        {t('autoOrdering.noRules')}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* ============================================================ */}
      {/* SECTION 2 — Auto-Approval Dashboard                          */}
      {/* ============================================================ */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">{t('autoOrdering.dashboard')}</Typography>
          <Button
            variant="contained"
            color="success"
            startIcon={<RunIcon />}
            onClick={() => runAutoApprovalMutation.mutate()}
            disabled={runAutoApprovalMutation.isPending}
          >
            {runAutoApprovalMutation.isPending ? (
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
            ) : null}
            {t('autoOrdering.runNow')}
          </Button>
        </Box>

        {/* KPI Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Auto-Approval Rate */}
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('autoOrdering.approvalRate')}
              </Typography>
              {rateLoading ? (
                <CircularProgress size={60} />
              ) : (
                <Box sx={{ position: 'relative', display: 'inline-flex', my: 1 }}>
                  <CircularProgress
                    variant="determinate"
                    value={rateData?.approval_rate ?? 0}
                    size={80}
                    thickness={6}
                    color="success"
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant="h6" fontWeight="bold">
                      {(rateData?.approval_rate ?? 0).toFixed(0)}%
                    </Typography>
                  </Box>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Orders Auto-Approved Today */}
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('autoOrdering.approvedToday')}
              </Typography>
              {rateLoading ? (
                <CircularProgress size={40} />
              ) : (
                <Typography variant="h3" fontWeight="bold" color="success.main" sx={{ my: 1 }}>
                  {rateData?.approved_today ?? 0}
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Orders Pending Manual Review */}
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('autoOrdering.pendingReview')}
              </Typography>
              {rateLoading ? (
                <CircularProgress size={40} />
              ) : (
                <Typography variant="h3" fontWeight="bold" color="warning.main" sx={{ my: 1 }}>
                  {rateData?.pending_review ?? 0}
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Recent Approval Log */}
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('autoOrdering.recentLogs')}
        </Typography>

        {logsError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('autoOrdering.errorLoadingLogs')}
          </Alert>
        )}

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('autoOrdering.logOrder')}</TableCell>
                <TableCell>{t('autoOrdering.logProduct')}</TableCell>
                <TableCell>{t('autoOrdering.logDecision')}</TableCell>
                <TableCell>{t('autoOrdering.logReason')}</TableCell>
                <TableCell>{t('autoOrdering.logRule')}</TableCell>
                <TableCell align="right">{t('autoOrdering.logQuantity')}</TableCell>
                <TableCell align="right">{t('autoOrdering.logValue')}</TableCell>
                <TableCell>{t('autoOrdering.logDate')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logsLoading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : logs && logs.length > 0 ? (
                logs.map((log) => {
                  const chipProps = getDecisionChipProps(log.decision);
                  return (
                    <TableRow key={log.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {log.order_id}
                        </Typography>
                      </TableCell>
                      <TableCell>{log.product_name}</TableCell>
                      <TableCell>
                        <Chip
                          label={chipProps.label}
                          color={chipProps.color}
                          icon={chipProps.icon}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {log.reason}
                        </Typography>
                      </TableCell>
                      <TableCell>{log.rule_name}</TableCell>
                      <TableCell align="right">{log.quantity.toLocaleString()}</TableCell>
                      <TableCell align="right">{formatCurrency(log.value_mad)}</TableCell>
                      <TableCell>{formatDate(log.created_at)}</TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Box sx={{ py: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('autoOrdering.noLogs')}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* ============================================================ */}
      {/* Create / Edit Rule Dialog                                     */}
      {/* ============================================================ */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRule ? t('autoOrdering.editRule') : t('autoOrdering.createRule')}
        </DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" onClose={() => setFormError(null)} sx={{ mb: 2, mt: 1 }}>
              {formError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Rule Name */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('autoOrdering.fieldRuleName')}
                value={formData.rule_name}
                onChange={(e) => handleFieldChange('rule_name', e.target.value)}
                required
              />
            </Grid>

            {/* Category & Brand */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('autoOrdering.fieldCategory')}
                value={formData.applies_to_category}
                onChange={(e) => handleFieldChange('applies_to_category', e.target.value)}
                helperText={t('autoOrdering.fieldCategoryHelper')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('autoOrdering.fieldBrand')}
                value={formData.applies_to_brand}
                onChange={(e) => handleFieldChange('applies_to_brand', e.target.value)}
                helperText={t('autoOrdering.fieldBrandHelper')}
              />
            </Grid>

            {/* Max Order Value & Quantity */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label={t('autoOrdering.fieldMaxValue')}
                value={formData.max_order_value_mad}
                onChange={(e) => handleFieldChange('max_order_value_mad', parseFloat(e.target.value) || 0)}
                InputProps={{ endAdornment: <Typography variant="caption">{currency}</Typography> }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label={t('autoOrdering.fieldMaxQty')}
                value={formData.max_order_quantity}
                onChange={(e) => handleFieldChange('max_order_quantity', parseInt(e.target.value) || 0)}
              />
            </Grid>

            {/* Coverage Days */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label={t('autoOrdering.fieldMinCoverage')}
                value={formData.min_coverage_days}
                onChange={(e) => handleFieldChange('min_coverage_days', parseInt(e.target.value) || 0)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label={t('autoOrdering.fieldMaxCoverage')}
                value={formData.max_coverage_days}
                onChange={(e) => handleFieldChange('max_coverage_days', parseInt(e.target.value) || 0)}
              />
            </Grid>

            {/* Priority */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label={t('autoOrdering.fieldPriority')}
                value={formData.priority}
                onChange={(e) => handleFieldChange('priority', parseInt(e.target.value) || 0)}
                helperText={t('autoOrdering.fieldPriorityHelper')}
              />
            </Grid>

            {/* Switches */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enforce_moq}
                    onChange={(e) => handleFieldChange('enforce_moq', e.target.checked)}
                  />
                }
                label={t('autoOrdering.fieldEnforceMOQ')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.round_to_case_pack}
                    onChange={(e) => handleFieldChange('round_to_case_pack', e.target.checked)}
                  />
                }
                label={t('autoOrdering.fieldCasePack')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.auto_transmit_to_erp}
                    onChange={(e) => handleFieldChange('auto_transmit_to_erp', e.target.checked)}
                  />
                }
                label={t('autoOrdering.fieldAutoTransmit')}
              />
            </Grid>

            {/* Active */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => handleFieldChange('is_active', e.target.checked)}
                  />
                }
                label={t('autoOrdering.fieldIsActive')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('autoOrdering.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSaveRule}
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {createMutation.isPending || updateMutation.isPending ? (
              <CircularProgress size={20} sx={{ mr: 1 }} />
            ) : null}
            {editingRule ? t('autoOrdering.update') : t('autoOrdering.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================================ */}
      {/* Delete Confirmation Dialog                                    */}
      {/* ============================================================ */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>{t('autoOrdering.deleteTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('autoOrdering.deleteConfirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>{t('autoOrdering.cancel')}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDelete}
            disabled={deleteMutation.isPending}
          >
            {t('autoOrdering.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================================ */}
      {/* Notification Snackbar                                         */}
      {/* ============================================================ */}
      <Snackbar
        open={!!notification}
        autoHideDuration={6000}
        onClose={() => setNotification(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setNotification(null)}
          severity={notification?.severity || 'success'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AutoOrderingSettings;
